import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Timestamp, doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { useCompanyAiAgents } from '../../hooks/useCompanyAiAgents';
import {
  AGILE_TASK_STATUSES,
  AGILE_TASK_TYPES,
  DEFAULT_AGILE_STATUS,
  DEFAULT_AGILE_TYPE,
} from '../../constants/agileTasks';
import {
  AI_ORCHESTRATION_DOC_ID,
  buildTaskAssignmentFromKey,
  createDefaultAiOrchestrationSettings,
  normalizeAiOrchestrationSettings,
  taskAssigneeKey,
} from '../../lib/agileAiOrchestration';
import {
  agentToAssigneeOption,
  assigneeOptionLabel,
  humanToAssigneeOption,
} from '../../lib/ticketAssignees';
import {
  createLinkedEscalationTicket,
  fetchLinkedTicketsByIds,
  linkedTicketStatusLabel,
} from '../../lib/taskTicketLinks';
import { appendTicketLog, participantsForTicket } from '../../lib/ticketLogs';
import { appendTicketInboxAlert } from '../../lib/ticketAlerts';
import { DASHBOARD_TAB_PATHS } from '../../lib/dashboardRoutes';

const EMPTY_FORM = {
  title: '',
  description: '',
  type: DEFAULT_AGILE_TYPE,
  status: DEFAULT_AGILE_STATUS,
  assigneeKey: '',
  dueDate: '',
};

function toDatetimeLocalValue(dueDate) {
  if (!dueDate) return '';
  const d = typeof dueDate.toDate === 'function' ? dueDate.toDate() : new Date(dueDate);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Create / edit an agile task (`tasks` collection).
 */
export default function TaskModal({
  open,
  onClose,
  task,
  companyId,
  userData,
  companyUsers = [],
  saving = false,
  onSave,
  readOnly = false,
  aiOrchestration: aiOrchestrationProp = null,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [liveTask, setLiveTask] = useState(null);
  const [linkedTickets, setLinkedTickets] = useState([]);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkBody, setLinkBody] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linking, setLinking] = useState(false);
  const [loadedOrchestration, setLoadedOrchestration] = useState(
    createDefaultAiOrchestrationSettings,
  );

  const { agents } = useCompanyAiAgents(companyId);
  const aiOrchestration = aiOrchestrationProp || loadedOrchestration;

  const activeAgents = useMemo(
    () => agents.filter((agent) => !agent.status || agent.status === 'active'),
    [agents],
  );

  const uid = auth.currentUser?.uid;
  const email = auth.currentUser?.email || userData?.email || '';
  const role = userData?.role || '';
  const resolvedTask = liveTask || task;

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        type: task.type || DEFAULT_AGILE_TYPE,
        status: task.status || DEFAULT_AGILE_STATUS,
        assigneeKey: taskAssigneeKey(task),
        dueDate: toDatetimeLocalValue(task.dueDate),
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, task]);

  useEffect(() => {
    if (!open || !companyId || aiOrchestrationProp) return undefined;

    const orchestrationRef = doc(
      db,
      'companies',
      companyId,
      'agile_settings',
      AI_ORCHESTRATION_DOC_ID,
    );

    const unsub = onSnapshot(
      orchestrationRef,
      (snap) => {
        setLoadedOrchestration(
          normalizeAiOrchestrationSettings(snap.exists() ? snap.data() : null),
        );
      },
      (err) => {
        console.warn('TaskModal ai orchestration:', err);
      },
    );

    return () => unsub();
  }, [open, companyId, aiOrchestrationProp]);

  useEffect(() => {
    if (!open || !task?.id) {
      setLiveTask(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, 'tasks', task.id),
      (snap) => {
        setLiveTask(snap.exists() ? { id: snap.id, ...snap.data() } : task);
      },
      (err) => {
        console.warn('TaskModal task listener:', err);
        setLiveTask(task);
      },
    );
    return () => unsub();
  }, [open, task]);

  const loadLinkedTickets = useCallback(async () => {
    const ids = resolvedTask?.linkedTicketIds || [];
    const companyId = userData?.companyId || resolvedTask?.companyId || '';
    if (!ids.length) {
      setLinkedTickets([]);
      return;
    }
    setLinkedLoading(true);
    try {
      const rows = await fetchLinkedTicketsByIds(db, ids, companyId);
      setLinkedTickets(rows);
    } catch (e) {
      console.error('TaskModal linked tickets:', e);
      setLinkedTickets([]);
    } finally {
      setLinkedLoading(false);
    }
  }, [resolvedTask?.linkedTicketIds, resolvedTask?.companyId, userData?.companyId]);

  useEffect(() => {
    if (!open || !resolvedTask?.id) {
      setLinkedTickets([]);
      return;
    }
    loadLinkedTickets();
  }, [open, resolvedTask?.id, resolvedTask?.linkedTicketIds, loadLinkedTickets]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly || !form.title.trim()) return;
    const dueDate = form.dueDate
      ? Timestamp.fromDate(new Date(form.dueDate))
      : null;
    const assignment = buildTaskAssignmentFromKey(form.assigneeKey);
    onSave?.({
      title: form.title.trim(),
      description: form.description.trim(),
      type: form.type,
      status: form.status,
      assigneeUid: assignment.assigneeUid,
      assigneeAgentId: assignment.assigneeAgentId,
      isAssignedToAi: assignment.isAssignedToAi,
      dueDate,
    });
  };

  const isAiAssignee = form.assigneeKey.startsWith('ai:');
  const madText = (aiOrchestration.masterArchitectureDocument || '').trim();

  const madPayloadPreview = useMemo(() => {
    if (!isAiAssignee || !aiOrchestration.enabled) return '';
    const sections = [];
    if (form.description.trim()) {
      sections.push(`## Task description\n${form.description.trim()}`);
    }
    sections.push(
      madText ?
        `## Master Architecture Document (MAD)\n${madText}` :
        '## Master Architecture Document (MAD)\n(empty — Scrum Master will populate)',
    );
    return sections.join('\n\n');
  }, [isAiAssignee, aiOrchestration.enabled, form.description, madText]);

  const openLinkTicketDialog = () => {
    setLinkBody(resolvedTask?.description || '');
    setLinkError('');
    setLinkDialogOpen(true);
  };

  const handleCreateLinkedTicket = async () => {
    if (!resolvedTask?.id || !companyId || !uid) return;
    setLinking(true);
    setLinkError('');
    try {
      const { ticketId, title } = await createLinkedEscalationTicket(db, {
        task: resolvedTask,
        companyId,
        body: linkBody,
        uid,
        email,
      });
      const ticketStub = { id: ticketId, reporterUid: uid, assigneeUid: resolvedTask.assigneeUid || null };
      await appendTicketLog(db, {
        companyId,
        ticketId,
        action: 'ticket_created',
        summary: `Ticket created from task: ${title}`,
        meta: { linkedTaskId: resolvedTask.id, source: 'task_escalation' },
        actorUid: uid,
        actorEmail: email,
        actorRole: role,
        participantUids: participantsForTicket(ticketStub, uid),
      });
      try {
        await appendTicketInboxAlert(db, {
          companyId,
          ticketId,
          title: `New ticket: ${title}`,
          message: `Escalated from agile task "${resolvedTask.title || 'Task'}".`,
          severity: 'info',
          ruleCode: 'TICKET_CREATED',
          ticketAction: 'created',
          actorUid: uid,
          actorEmail: email,
        });
      } catch (alertErr) {
        console.warn('task escalation alert:', alertErr);
      }
      setLinkDialogOpen(false);
      await loadLinkedTickets();
    } catch (e) {
      console.error('create linked ticket:', e);
      setLinkError(e.message || 'Could not create linked ticket.');
    } finally {
      setLinking(false);
    }
  };

  const escalationTitle = resolvedTask?.title
    ? `[Task Escalation] ${resolvedTask.title}`
    : '';

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <DialogTitle>{task ? (readOnly ? 'View task' : 'Edit task') : 'New task'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              required
              autoFocus={!readOnly}
              fullWidth
              disabled={readOnly}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              multiline
              minRows={3}
              fullWidth
              disabled={readOnly}
            />
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel id="task-type-label">Type</InputLabel>
              <Select
                labelId="task-type-label"
                label="Type"
                value={form.type}
                onChange={(e) => setField('type', e.target.value)}
              >
                {AGILE_TASK_TYPES.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel id="task-status-label">Status</InputLabel>
              <Select
                labelId="task-status-label"
                label="Status"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
              >
                {AGILE_TASK_STATUSES.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Due date"
              type="datetime-local"
              value={form.dueDate}
              onChange={(e) => setField('dueDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={readOnly ? undefined : "Syncs to the assignee's Google Calendar when connected"}
              disabled={readOnly}
            />
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel id="task-assignee-label">Assignee</InputLabel>
              <Select
                labelId="task-assignee-label"
                label="Assignee"
                value={form.assigneeKey}
                onChange={(e) => setField('assigneeKey', e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {companyUsers.length > 0 && (
                  <ListSubheader>Team members</ListSubheader>
                )}
                {companyUsers.map((u) => {
                  const option = humanToAssigneeOption(u);
                  return (
                    <MenuItem key={u.uid} value={`human:${u.uid}`}>
                      {assigneeOptionLabel(option)}
                    </MenuItem>
                  );
                })}
                {activeAgents.length > 0 && (
                  <ListSubheader>AI Agents</ListSubheader>
                )}
                {activeAgents.map((agent) => {
                  const option = agentToAssigneeOption(agent);
                  return (
                    <MenuItem key={agent.id} value={`ai:${agent.id}`}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <SmartToyOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <span>{assigneeOptionLabel(option)}</span>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {isAiAssignee && aiOrchestration.enabled && (
              <Alert
                severity="info"
                icon={<SmartToyOutlinedIcon fontSize="inherit" />}
                sx={{
                  '& .MuiAlert-message': { width: '100%' },
                  bgcolor: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.12)' : 'primary.50',
                }}
              >
                <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                  AI task payload — MAD appended (read-only preview)
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: (t) =>
                      t.palette.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'grey.100',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 140,
                    overflow: 'auto',
                  }}
                >
                  {madPayloadPreview}
                </Box>
              </Alert>
            )}

            {resolvedTask?.id && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Linked tickets
                    </Typography>
                    {!readOnly && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={openLinkTicketDialog}
                        disabled={linking}
                      >
                        Create linked ticket
                      </Button>
                    )}
                  </Stack>
                  {linkedLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : linkedTickets.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      No linked support tickets yet.
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {linkedTickets.map((ticket) => (
                        <Chip
                          key={ticket.id}
                          icon={<ConfirmationNumberIcon />}
                          clickable
                          onClick={() => navigate(DASHBOARD_TAB_PATHS.Tickets, {
                            state: { openTicketId: ticket.id },
                          })}
                          label={`${ticket.title || ticket.id} · ${linkedTicketStatusLabel(ticket)}`}
                          variant="outlined"
                          size="small"
                          sx={{ maxWidth: '100%' }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} disabled={saving}>
              {readOnly ? 'Close' : 'Cancel'}
            </Button>
            {!readOnly && (
              <Button type="submit" variant="contained" disabled={saving || !form.title.trim()}>
                {saving ? 'Saving…' : task ? 'Save changes' : 'Create task'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={linkDialogOpen}
        onClose={linking ? undefined : () => setLinkDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Create linked ticket</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {linkError && <Alert severity="error">{linkError}</Alert>}
            <TextField
              label="Ticket title"
              value={escalationTitle}
              fullWidth
              disabled
              helperText="Auto-generated from this task"
            />
            <TextField
              label="Description"
              value={linkBody}
              onChange={(e) => setLinkBody(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder="Describe what support needs to handle…"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)} disabled={linking}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateLinkedTicket} disabled={linking}>
            {linking ? 'Creating…' : 'Create ticket'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
